import type { CustomerProfile } from '../../types'
import { createAddressId, normalizeAddressBook } from './addressBookNormalization'
import type { AddressBookEntry } from './addressBookTypes'

const ADDRESS_BOOK_STORAGE_PREFIX = 'emporion.customer.address-book.v1'

export const toStorageKey = (profile: CustomerProfile) =>
  `${ADDRESS_BOOK_STORAGE_PREFIX}:${(profile.email || 'guest').trim().toLowerCase() || 'guest'}`

const sanitizeAddressBookEntry = (entry: unknown): AddressBookEntry | null => {
  if (!entry || typeof entry !== 'object') return null
  const candidate = entry as Partial<AddressBookEntry>
  const id = typeof candidate.id === 'string' && candidate.id.trim() ? candidate.id.trim() : createAddressId()

  return {
    id,
    label: typeof candidate.label === 'string' ? candidate.label.trim() : '',
    firstName: typeof candidate.firstName === 'string' ? candidate.firstName.trim() : '',
    lastName: typeof candidate.lastName === 'string' ? candidate.lastName.trim() : '',
    addressLine: typeof candidate.addressLine === 'string' ? candidate.addressLine.trim() : '',
    city: typeof candidate.city === 'string' ? candidate.city.trim() : '',
    postalCode: typeof candidate.postalCode === 'string' ? candidate.postalCode.trim() : '',
    province: typeof candidate.province === 'string' ? candidate.province.trim() : '',
    country: typeof candidate.country === 'string' && candidate.country.trim() ? candidate.country.trim() : 'IT',
    phone: typeof candidate.phone === 'string' ? candidate.phone.trim() : '',
    useShipping: Boolean(candidate.useShipping),
    useBilling: Boolean(candidate.useBilling),
    defaultShipping: Boolean(candidate.defaultShipping),
    defaultBilling: Boolean(candidate.defaultBilling),
  }
}

export const readAddressBookFromStorage = (storageKey: string) => {
  if (typeof window === 'undefined') return []

  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return normalizeAddressBook(
      parsed.map(sanitizeAddressBookEntry).filter((entry): entry is AddressBookEntry => entry !== null),
    )
  } catch {
    return []
  }
}

export const writeAddressBookToStorage = (storageKey: string, addressBook: AddressBookEntry[]) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(addressBook))
  } catch {
    // Ignore localStorage write errors to keep UX non-blocking.
  }
}
